import dayjs from 'dayjs'
import { createSignal, createEffect, onMount, createMemo, For, batch } from 'solid-js'
import { Portal } from 'solid-js/web'
import { AddOutline, CreateOutline, TrashOutline, EyeOutline, Refresh3Line, CheckmarkCircleOutline, CloseCircleOutline } from '@/icons'
import { res2json } from '@/utils'
import { paginator, randomUsername, randomNickname, randomPassword } from '@lib/utils'
import { addToast } from '@/components/Toast'
import account from '@/store/account'

const roles = {
  admin: '管理员',
  supervisor: '监管员'
}

const [result, setResult] = createSignal({})

export default function StaffUsers() {
  const [editUser, setEditUser] = createSignal({})
  const [selected, setSelected] = createSignal([])

  const allSelected = createMemo(() => {
    const sl = selected().length
    const rl = (result().rows ?? []).length
    return sl && sl === rl
  })

  const query = new URLSearchParams(document.location.search)
  const [loading, setLoading] = createSignal(true)
  const [sending, setSending] = createSignal(false)

  const homeLink = () => {
    const query = new URLSearchParams(document.location.search)
    query.delete('lt')
    query.delete('gt')
    return `${document.location.pathname}?${query.toString()}`
  }
  const prevLink = createMemo(() => paginator.prevLink(result()))
  const nextLink = createMemo(() => paginator.nextLink(result()))
  const lastLink = createMemo(() => paginator.lastLink(result()))
  const hasPaginator = createMemo(() => prevLink() || nextLink())

  onMount(() => {
    fetch(`/api/staff-users?${query.toString()}`).then(res2json).then(r => { setResult(r.data ?? {}) }).finally(() => { setLoading(false) })
  })

  function openEditStaffUserDialog(user) {
    setEditUser(user)
    window.editStaffUser.showModal()
  }

  function toggleAllSelection(val) {
    setSelected(val ? (result().rows ?? []).map(e => e.id) : [])
  }
  function toggleSelection(row) {
    setSelected(e => {
      const i = e.indexOf(row.id)
      if (i > -1) {
        e.splice(i, 1)
        return [...e]
      }
      return [...new Set([...e, row.id])]
    })
  }

  async function deleteUser(row) {
    if (!confirm(`确定要删除用户 ${row.username} 吗?`)) {
      return
    }
    return fetch(`/api/staff-users/${row.id}`, { method: 'DELETE' }).then(res2json).then(r => {
      if (r.status === 400) {
        addToast({ level: 'error', message: r.data.default })
      } else {
        document.location.reload()
      }
    })
  }

  async function batchOp(action) {
    setSending(true)
    fetch(`/api/staff-users/batch-${action}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids: selected() })
    }).then(res2json).then(r => {
      document.location.reload()
    }).finally(() => { setSending(false) })
  }

  function ActionPanel() {
    return (
      <>
        {!selected().length ? (<></>) : (
          <div class="sticky bottom-0 flex items-center gap-4 bg-base-100">
            <button type="button" onClick={() => batchOp('enable')} class="btn" disabled={sending()}>启用</button>
            <button type="button" onClick={() => batchOp('disable')} class="btn" disabled={sending()}>禁用</button>
          </div>
        )}
      </>
    )
  }

  return (
    <>
      <div class="flex flex-col gap-4">
        <div class="breadcrumbs text-sm">
          <ul>
            <li><a href="/home">后台首页</a></li>
            <li>后台用户</li>
          </ul>
        </div>
        <div class="flex items-center justify-between">
          <h2 class="text-xl">后台用户</h2>
          <button type="button" onClick={() => window.createStaffUser.showModal()} class="btn btn-success text-white"><span class="w-5"><AddOutline /></span>创建后台用户</button>
        </div>
        <div class="overflow-x-auto">
          <table class="table whitespace-nowrap">
            <thead>
              <tr class="bg-base-200">
                <th class="px-2 lg:px-3"><input type="checkbox" class="checkbox" checked={allSelected()} onChange={e => toggleAllSelection(e.target.checked)} /></th>
                <th class="px-2 lg:px-3">用户名(昵称)</th>
                <th class="px-2 lg:px-3">角色</th>
                <th class="px-2 lg:px-3">状态</th>
                <th class="px-2 lg:px-3 text-right">创建时间</th>
                <th class="px-2 lg:px-3 text-right">最近登陆时间</th>
                <th class="px-2 lg:px-3"></th>
              </tr>
            </thead>
            <tbody>
              <For each={result().rows ?? []}>
                {(row) => (
                  <tr class="hover:bg-base-200">
                    <td class="px-2 lg:px-3"><input type="checkbox" class="checkbox" checked={selected().indexOf(row.id) > -1} onChange={e => { toggleSelection(row) }} /></td>
                    <td class="px-2 lg:px-3">{row.username}({row.nickname})</td>
                    <td class="px-2 lg:px-3">{roles[row.role] ?? row.role}</td>
                    <td class={row.is_disabled ? 'px-2 lg:px-3 text-error' : 'px-2 lg:px-3 text-success'}><div class="w-6">{row.is_disabled ? <CloseCircleOutline /> : <CheckmarkCircleOutline />}</div></td>
                    <td class="px-2 lg:px-3 text-right">{row.created_at ? dayjs(row.created_at * 1000).format('YYYY-MM-DD HH:mm:ss') : '-'}</td>
                    <td class="px-2 lg:px-3 text-right">{row.last_logged_in_at ? dayjs(row.last_logged_in_at * 1000).format('YYYY-MM-DD HH:mm:ss') : '-'}</td>
                    <td class="px-2 lg:px-3">
                      <div class="flex items-center justify-end">
                        {account().agent !== row.username && account().username !== row.username && (
                          <div class="tooltip" data-tip="删除">
                            <button type="button" onClick={() => deleteUser(row)} class="btn btn-ghost btn-sm"><span class="w-5"><TrashOutline /></span></button>
                          </div>
                        )}
                        <div class="tooltip" data-tip="编辑">
                          <button type="button" onClick={() => openEditStaffUserDialog(row)} class="btn btn-ghost btn-sm"><span class="w-5"><CreateOutline /></span></button>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
        </div>
        <ActionPanel />
        {hasPaginator() && (
          <div class="flex items-center justify-end join">
            <button type="button" onClick={() => document.location.href = homeLink()} class="btn join-item font-normal" disabled={!prevLink()}>首页</button>
            <button type="button" onClick={() => document.location.href = prevLink()} class="btn join-item font-normal" disabled={!prevLink()}>上一页</button>
            <button type="button" onClick={() => document.location.href = nextLink()} class="btn join-item font-normal" disabled={!nextLink()}>下一页</button>
            <button type="button" onClick={() => document.location.href = lastLink()} class="btn join-item font-normal" disabled={!nextLink()}>尾页</button>
          </div>
        )}
      </div>

      <Portal mount={document.body}>
        {loading() && (<div class="fixed left-2 right-2 bottom-0"><progress class="progress progress-success w-full"></progress></div>)}

        <CreateStaffUserDialog />
        <EditStaffUserDialog user={editUser} />
      </Portal>
    </>
  )
}

function CreateStaffUserDialog() {
  const [username, setUsername] = createSignal('')
  const [nickname, setNickname] = createSignal('')
  const [password, setPassword] = createSignal('')
  const [role, setRole] = createSignal('admin')

  const [sending, setSending] = createSignal(false)
  const [errors, setErrors] = createSignal({})

  onMount(() => {
    new MutationObserver((e) => {
      if (e[0].target.open) {
        batch(() => {
          setUsername(randomUsername())
          setNickname(randomNickname())
          setRole('supervisor')
          setPassword(randomPassword())

          setErrors({})
        })
      }
    }).observe(window.createStaffUser, { attributes: true })
  })

  async function submit(e) {
    e.preventDefault()
    if (sending()) {
      return
    }

    setSending(true)
    return fetch('/api/staff-users', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        username: username().trim(),
        nickname: nickname().trim(),
        role: role(),
        password: password()
      })
    }).then(res2json).then(r => {
      if (r.status >= 400) {
        setErrors(r.data)
      } else {
        document.location.reload()
      }
    }).finally(() => { setSending(false) })
  }

  function cancel() {
    window.createStaffUser.close()
  }

  return (
    <dialog id="createStaffUser" class="modal">
      <form onSubmit={submit} class="modal-box flex flex-col gap-4">
        <button type="button" onClick={() => cancel()} class="btn btn-sm btn-circle btn-ghost absolute right-2 top-2" disabled={sending()}>✕</button>
        <h3 class="text-lg">创建后台用户</h3>
        <div class="form-control">
          <div class="label"><span class="label-text">用户名</span></div>
          <div class="input input-bordered flex items-center">
            <input type="text" value={username()} onChange={e => setUsername(e.target.value)} class="grow" placeholder="请输入用户名" required />
            <div class="tooltip" data-tip="随机生成">
              <button type="button" onClick={() => setUsername(randomUsername())} class="btn btn-ghost btn-sm"><span class="w-6"><Refresh3Line /></span></button>
            </div>
          </div>
          {errors().username && (<div class="label"><span class="label-text-alt text-error">{errors().username}</span></div>)}
        </div>
        <div class="form-control">
          <div class="label"><span class="label-text">昵称</span></div>
          <div class="input input-bordered flex items-center">
            <input type="text" value={nickname()} onChange={e => setNickname(e.target.value)} class="grow" placeholder="请输入昵称" required />
            <div class="tooltip" data-tip="随机生成">
              <button type="button" onClick={() => setNickname(randomNickname())} class="btn btn-ghost btn-sm"><span class="w-6"><Refresh3Line /></span></button>
            </div>
          </div>
          {errors().nickname && (<div class="label"><span class="label-text-alt text-error">{errors().nickname}</span></div>)}
        </div>
        <div class="form-control">
          <div class="label"><span class="label-text">角色</span></div>
          <select name="game" value={role()} onChange={e => setRole(e.target.value)} class="select select-bordered" required>
            <option value="" selected disabled>请选择一个角色</option>
            {Object.entries(roles).map(([k, v]) => (<option value={k}>{v}</option>))}
          </select>
          {errors().role && (<div class="label"><span class="label-text-alt text-error">{errors().role}</span></div>)}
        </div>
        <div class="form-control">
          <div class="label"><span class="label-text">登陆密码</span></div>
          <div class="input input-bordered flex items-center">
            <input type="text" value={password()} onChange={e => setPassword(e.target.value)} class="grow" placeholder="请输入登陆密码" required />
            <div class="tooltip" data-tip="随机生成">
              <button type="button" onClick={() => setPassword(randomPassword())} class="btn btn-ghost btn-sm"><span class="w-6"><Refresh3Line /></span></button>
            </div>
          </div>
          {errors().password && (<div class="label"><span class="label-text-alt text-error">{errors().password}</span></div>)}
        </div>
        {errors().default && (<div class="text-error">{errors().default}</div>)}
        <div class="modal-action flex gap-4">
          <button type="button" onClick={() => cancel()} class="btn px-8" disabled={sending()}>取消</button>
          <button type="submit" class="btn btn-success text-white px-8" disabled={sending()}>确定</button>
        </div>
      </form>
    </dialog>
  )
}

function EditStaffUserDialog({ user }) {
  const [nickname, setNickname] = createSignal('')
  const [password, setPassword] = createSignal('')
  const [role, setRole] = createSignal('')
  const [isDisabled, setIsDisabled] = createSignal(false)

  const [sending, setSending] = createSignal(false)
  const [errors, setErrors] = createSignal({})

  const [isEditable, setIsEditable] = createSignal(false)

  let elFocus

  onMount(() => {
    new MutationObserver((e) => {
      if (e[0].target.open) {
        batch(() => {
          setNickname(user().nickname)
          setPassword('')
          setRole(user().role)
          setIsDisabled(user().is_disabled)

          setErrors({})
          setIsEditable(account().agent !== user().username && account().username !== user().username)
        })
        elFocus.focus()
      }
    }).observe(window.editStaffUser, { attributes: true })
  })

  async function submit(e) {
    e.preventDefault()
    if (sending()) {
      return
    }

    setSending(true)
    return fetch(`/api/staff-users/${user().id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        nickname: nickname().trim(),
        role: role(),
        password: password(),
        is_disabled: isDisabled(),
      })
    }).then(res2json).then(r => {
      if (r.status >= 400) {
        setErrors(r.data)
      } else {
        document.location.reload()
      }
    }).finally(() => { setSending(false) })
  }

  function cancel() {
    window.editStaffUser.close()
  }

  return (
    <dialog id="editStaffUser" class="modal">
      <form onSubmit={submit} class="modal-box flex flex-col gap-4">
        <button type="button" onClick={() => cancel()} class="btn btn-sm btn-circle btn-ghost absolute right-2 top-2" disabled={sending()}>✕</button>
        <h3 class="text-lg">编辑后台用户</h3>
        <div class="form-control">
          <div class="label"><span class="label-text">用户名</span></div>
          <div class="input input-bordered flex items-center">{user().username}</div>
        </div>
        <div class="form-control">
          <div class="label"><span class="label-text">昵称</span></div>
          <div class="input input-bordered flex items-center">
            <input ref={elFocus} type="text" value={nickname()} onChange={e => setNickname(e.target.value)} class="grow" placeholder="请输入昵称" required />
            <div class="tooltip" data-tip="随机生成">
              <button type="button" onClick={() => setNickname(randomNickname())} class="btn btn-ghost btn-sm"><span class="w-6"><Refresh3Line /></span></button>
            </div>
          </div>
          {errors().nickname && (<div class="label"><span class="label-text-alt text-error">{errors().nickname}</span></div>)}
        </div>
        <div class="form-control">
          <div class="label"><span class="label-text">角色</span></div>
          {!isEditable() && (<div class="input input-bordered flex items-center">{roles[user().role]}</div>)}
          {isEditable() && (
            <>
              <select name="game" value={role()} onChange={e => setRole(e.target.value)} class="select select-bordered" required>
                <option value="" selected disabled>请选择一个角色</option>
                {Object.entries(roles).map(([k, v]) => (<option value={k} selected={role() === k}>{v}</option>))}
              </select>
              {errors().role && (<div class="label"><span class="label-text-alt text-error">{errors().role}</span></div>)}
            </>
          )}
        </div>
        <div class="form-control">
          <div class="label"><span class="label-text">修改密码</span></div>
          <div class="input input-bordered flex items-center">
            <input type="text" value={password()} onChange={e => setPassword(e.target.value)} class="grow" placeholder="不修改请留空" />
            <div class="tooltip" data-tip="随机生成">
              <button type="button" onClick={() => setPassword(randomPassword())} class="btn btn-ghost btn-sm"><span class="w-6"><Refresh3Line /></span></button>
            </div>
          </div>
          {errors().password && (<div class="label"><span class="label-text-alt text-error">{errors().password}</span></div>)}
        </div>
        {isEditable() && (
          <div>
            <label class="label cursor-pointer inline-flex gap-2">
              <input type="checkbox" onChange={e => setIsDisabled(!e.target.checked)} class="checkbox" checked={!isDisabled()} />
              <span class="label-text">启用账号</span>
            </label>
          </div>
        )}
        {errors().default && (<div class="text-error">{errors().default}</div>)}
        <div class="modal-action flex gap-4">
          <button type="button" onClick={() => cancel()} class="btn px-8" disabled={sending()}>取消</button>
          <button type="submit" class="btn btn-success text-white px-8" disabled={sending()}>确定</button>
        </div>
      </form>
    </dialog>
  )
}
